import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import COLORS from '@/resources/style/Themes';
import { ErrorStringKeys, StringKeys } from '@/resources/strings/StringKeys';
import { NavigatorDataGrid } from '@/components/data/NavigatorDataGrid';
import { useToast } from '@/context_providers/toast_context/ToastContext';
import { useOrganizationUpdaterProvider } from '../../../context_providers/organization_context/OrganizationProvider';
import { processJoinRequests } from '@/repository/JoinRequestsDAO';
import {
  OrgJoinRequestItem,
  JoinRequestAction,
} from '@/../generated-client/models';

type JoinRequestRow = {
  id: string; // auth_id
  name: string;
  phone: string;
  dateRequested: string;
  role: string;
  group: string;
  selected: boolean;
  authId: string;
};

const selectSx = {
  minWidth: 100,
  height: 40,
  borderRadius: 2,
  backgroundColor: '#e0e0e0',
  fontSize: 14,
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiSelect-select': {
    py: 0.5,
  },
};

const actionTextSx = {
  fontSize: 14,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
};

const processingOverlaySx = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(235, 235, 235, 0.65)',
  zIndex: 2,
};

const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const mapJoinRequestToRow = (
  item: OrgJoinRequestItem,
  defaultGroup: string,
): JoinRequestRow => ({
  id: item.auth_id,
  authId: item.auth_id,
  name: `${item.first_name} ${item.last_name}`,
  phone: item.phone_number,
  dateRequested: formatDate(item.requested_timestamp),
  role: 'Mobile User',
  group: defaultGroup,
  selected: false,
});

const OrganizationJoinRequests = () => {
  const {
    organization,
    groups,
    joinRequests,
    joinRequestsStatus,
    joinRequestsError,
  } = useOrganizationUpdaterProvider();
  const { t } = useTranslation();
  const { showErrorToast, showSuccessToast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [rows, setRows] = useState<JoinRequestRow[]>([]);
  const [processing, setProcessing] = useState(false);
  // UX: SignalR drives the source-of-truth list, but it can lag behind successful API actions.
  // Track locally-hidden join requests so rows disappear immediately when we show a success toast.
  const [optimisticallyHiddenAuthIds, setOptimisticallyHiddenAuthIds] = useState<
    Set<string>
  >(() => new Set());

  const getSuccessToastMessage = (approve: boolean, count: number) => {
    if (count === 1) {
      return approve
        ? t(StringKeys.SUCCESSFULLY_APPROVED_JOIN_REQUEST)
        : t(StringKeys.SUCCESSFULLY_DENIED_JOIN_REQUEST);
    }

    return approve
      ? t(StringKeys.SUCCESSFULLY_APPROVED_JOIN_REQUESTS, { count })
      : t(StringKeys.SUCCESSFULLY_DENIED_JOIN_REQUESTS, { count });
  };

  const getFailureToastMessage = (
    approve: boolean,
    requestedCount: number,
    fallbackMessage?: string,
  ) => {
    if (fallbackMessage) {
      return fallbackMessage;
    }

    if (requestedCount === 1) {
      return approve
        ? t(ErrorStringKeys.FAILED_TO_APPROVE_JOIN_REQUEST)
        : t(ErrorStringKeys.FAILED_TO_DENY_JOIN_REQUEST);
    }

    return approve
      ? t(ErrorStringKeys.FAILED_TO_APPROVE_JOIN_REQUESTS, {
          count: requestedCount,
        })
      : t(ErrorStringKeys.FAILED_TO_DENY_JOIN_REQUESTS, {
          count: requestedCount,
        });
  };

  const groupOptions = useMemo(() => groups.map((g) => g.group_name), [groups]);
  const roleOptions = useMemo(() => {
    const roles = Object.keys(organization?.roles_with_features_default || {});
    return roles.length > 0 ? roles : ['Mobile User', 'Admin', 'Dispatch'];
  }, [organization]);

  useEffect(() => {
    const defaultGroup = groupOptions[0] || 'Default';

    setRows((prevRows) => {
      const previousByAuthId = new Map(
        prevRows.map((row) => [row.authId, row] as const),
      );

      const visibleJoinRequests = joinRequests.filter(
        (request) => !optimisticallyHiddenAuthIds.has(request.auth_id),
      );

      return visibleJoinRequests.map((request) => {
        const nextRow = mapJoinRequestToRow(request, defaultGroup);
        const previousRow = previousByAuthId.get(request.auth_id);

        if (!previousRow) {
          return nextRow;
        }

        return {
          ...nextRow,
          role: previousRow.role,
          group: previousRow.group,
          selected: previousRow.selected,
        };
      });
    });
  }, [groupOptions, joinRequests, optimisticallyHiddenAuthIds]);

  useEffect(() => {
    // Prune hidden ids once SignalR has removed them from the upstream list.
    setOptimisticallyHiddenAuthIds((prev) => {
      if (prev.size === 0) return prev;

      const upstreamIds = new Set(joinRequests.map((r) => r.auth_id));
      let changed = false;
      const next = new Set<string>();

      for (const id of prev) {
        if (upstreamIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [joinRequests]);

  const hideRowsByAuthId = (authIds: string[]) => {
    if (authIds.length === 0) return;

    const authIdSet = new Set(authIds);

    setOptimisticallyHiddenAuthIds((prev) => {
      const next = new Set(prev);
      for (const id of authIds) next.add(id);
      return next;
    });

    // Remove from the current rendered list immediately (effect above keeps it hidden on subsequent SignalR updates).
    setRows((prev) => prev.filter((row) => !authIdSet.has(row.authId)));
  };

  const loading =
    joinRequestsStatus === 'idle' || joinRequestsStatus === 'loading';

  const selectedRows = rows.filter((row) => row.selected);
  const allSelected = rows.length > 0 && selectedRows.length === rows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  const handleToggleAll = () => {
    const nextValue = !allSelected;
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        selected: nextValue,
      })),
    );
  };

  const handleToggleRow = (id: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, selected: !row.selected } : row,
      ),
    );
  };

  const handleRoleChange = (id: string, role: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, role } : row)),
    );
  };

  const handleGroupChange = (id: string, group: string) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, group } : row)),
    );
  };

  const handleProcessRequest = async (
    authIds: string[],
    approve: boolean,
    rowsToProcess: JoinRequestRow[],
  ) => {
    if (!organization?.org_code || authIds.length === 0) return;

    try {
      setProcessing(true);
      const actions: JoinRequestAction[] = authIds.map((authId) => {
        const row = rowsToProcess.find((r) => r.authId === authId);
        return {
          auth_id: authId,
          approve,
          group_name: row?.group || 'Default',
          role: row?.role || 'Mobile User',
        };
      });

      const result = await processJoinRequests(organization.org_code, actions);

      if (result.failed === 0) {
        hideRowsByAuthId(authIds);
        showSuccessToast(getSuccessToastMessage(approve, result.successful));
        return;
      }

      if (result.successful > 0 && result.failed > 0) {
        const successfulAuthIds = result.results
          .filter((requestResult) => requestResult.success)
          .map((requestResult) => requestResult.auth_id);

        hideRowsByAuthId(successfulAuthIds);
        showErrorToast(
          t(StringKeys.PROCESSED_JOIN_REQUESTS, {
            totalProcessed: result.total_processed,
            successful: result.successful,
            failed: result.failed,
          }),
        );
        return;
      }

      const firstFailureMessage = result.results.find(
        (requestResult) => !requestResult.success,
      )?.message;

      showErrorToast(
        getFailureToastMessage(approve, authIds.length, firstFailureMessage),
      );
    } catch (error) {
      console.error('Error processing join request:', error);
      showErrorToast(getFailureToastMessage(approve, authIds.length));
    } finally {
      setProcessing(false);
    }
  };

  const handleApprove = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (row) {
      handleProcessRequest([row.authId], true, [row]);
    }
  };

  const handleDeny = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (row) {
      handleProcessRequest([row.authId], false, [row]);
    }
  };

  const handleApproveSelected = () => {
    const authIds = selectedRows.map((row) => row.authId);
    handleProcessRequest(authIds, true, selectedRows);
  };

  const handleDenySelected = () => {
    const authIds = selectedRows.map((row) => row.authId);
    handleProcessRequest(authIds, false, selectedRows);
  };

  const columns: GridColDef<JoinRequestRow>[] = [
    {
      field: 'selected',
      headerName: '',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerClassName: 'first',
      cellClassName: 'first',
      renderHeader: () => (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={handleToggleAll}
          disabled={processing}
          sx={{
            p: 0,
            '&.Mui-checked': {
              color: COLORS.BLUE_CRAYOLA,
            },
            '&.MuiCheckbox-indeterminate': {
              color: COLORS.BLUE_CRAYOLA,
            },
          }}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={params.row.selected}
          onChange={() => handleToggleRow(params.row.id)}
          disabled={processing}
          sx={{
            p: 0,
            '&.Mui-checked': {
              color: COLORS.BLUE_CRAYOLA,
            },
          }}
        />
      ),
    },
    {
      field: 'user',
      headerName: t(StringKeys.USER),
      minWidth: 150,
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ py: 1 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: COLORS.SAFEGUARD_BLUE,
            }}
          >
            {params.row.name}
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              color: COLORS.GREY,
            }}
          >
            {params.row.phone}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'dateRequested',
      headerName: t(StringKeys.DATE_REQUESTED),
      flex: 1,
      minWidth: 150,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'role',
      headerName: t(StringKeys.ASSIGN_ROLE),
      flex: 1,
      minWidth: 150,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Select
          size="small"
          value={params.row.role}
          onChange={(e) => handleRoleChange(params.row.id, e.target.value)}
          disabled={processing}
          sx={selectSx}
        >
          {roleOptions.map((role) => (
            <MenuItem key={role} value={role}>
              {role}
            </MenuItem>
          ))}
        </Select>
      ),
    },
    {
      field: 'group',
      headerName: t(StringKeys.ASSIGN_GROUP),
      flex: 1,
      minWidth: 150,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Select
          size="small"
          value={params.row.group}
          onChange={(e) => handleGroupChange(params.row.id, e.target.value)}
          disabled={processing}
          sx={{ ...selectSx, minWidth: 90 }}
        >
          {(groupOptions.length > 0 ? groupOptions : ['Default']).map(
            (group) => (
              <MenuItem key={group} value={group}>
                {group}
              </MenuItem>
            ),
          )}
        </Select>
      ),
    },
    {
      field: 'action',
      headerName: t(StringKeys.ACTION),
      flex: 1,
      minWidth: 150,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Typography
            onClick={() => !processing && handleApprove(params.row.id)}
            sx={{
              ...actionTextSx,
              color: processing ? COLORS.GREY : COLORS.BLUE_CRAYOLA,
              ':hover': {
                color: processing ? COLORS.GREY : '#2b64af',
                fontWeight: 500,
              },
              cursor: processing ? 'not-allowed' : 'pointer',
            }}
          >
            {t(StringKeys.APPROVE)}
          </Typography>
          <Typography sx={{ color: COLORS.GREY }}>|</Typography>
          <Typography
            onClick={() => !processing && handleDeny(params.row.id)}
            sx={{
              ...actionTextSx,
              color: processing ? COLORS.GREY : COLORS.RED,
              ':hover': {
                color: processing ? COLORS.GREY : COLORS.BORDER_RED,
                fontWeight: 500,
              },
              cursor: processing ? 'not-allowed' : 'pointer',
            }}
          >
            {t(StringKeys.DENY)}
          </Typography>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        ...(!isMobile && {
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }),
      }}
    >
      <Typography sx={{ fontSize: 14, color: COLORS.GREY, fontWeight: 500 }}>
        {t(StringKeys.JOIN_REQUESTS)}
      </Typography>

      <Typography
        variant="h5"
        sx={{ color: COLORS.SAFEGUARD_BLUE, fontWeight: 500, mb: 1 }}
      >
        {organization?.company_name}
      </Typography>

      <Typography
        variant="subtitle2"
        sx={{ color: COLORS.GREY, display: 'block', mb: 2, fontWeight: 400 }}
      >
        {t(StringKeys.NEW_MEMBERS_JOIN_AS_CONNECT)}
      </Typography>

      {!isMobile ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Desktop: Bulk Actions + DataGrid */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography
                onClick={() => !processing && handleApproveSelected()}
                sx={{
                  ...actionTextSx,
                  color:
                    processing || selectedRows.length === 0
                      ? COLORS.GREY
                      : COLORS.BLUE_CRAYOLA,
                  cursor:
                    processing || selectedRows.length === 0
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                {t(StringKeys.APPROVE_SELECTED)}
              </Typography>
              <Typography sx={{ color: COLORS.GREY }}>|</Typography>
              <Typography
                onClick={() => !processing && handleDenySelected()}
                sx={{
                  ...actionTextSx,
                  color:
                    processing || selectedRows.length === 0
                      ? COLORS.GREY
                      : COLORS.RED,
                  cursor:
                    processing || selectedRows.length === 0
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                {t(StringKeys.DENY_SELECTED)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              position: 'relative',
              flex: 1,
              minHeight: 0,
            }}
          >
            {processing && (
              <Box sx={processingOverlaySx}>
                <CircularProgress />
              </Box>
            )}
            <NavigatorDataGrid<JoinRequestRow>
              rows={rows}
              columns={columns}
              getRowId={(row: JoinRequestRow) => row.id}
              initialPageSize={5}
              pageSizeOptions={[5, 10, 15]}
              height="100%"
              slots={{
                noRowsOverlay: () => (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      py: 8,
                    }}
                  >
                    {joinRequestsError ? (
                      <Typography
                        sx={{
                          color: COLORS.RED,
                          fontSize: 14,
                          fontWeight: 500,
                        }}
                      >
                        {t(StringKeys.UNABLE_TO_LOAD_JOIN_REQUESTS)}
                      </Typography>
                    ) : (
                      <>
                        <TaskAltIcon
                          sx={{
                            fontSize: 48,
                            color: COLORS.MODAL_HEADER_GRAY,
                            mb: 1,
                          }}
                        />
                        <Typography
                          sx={{
                            color: COLORS.MODAL_HEADER_GRAY,
                            fontSize: 14,
                          }}
                        >
                          {t(StringKeys.NO_PENDING_JOIN_REQUESTS)}
                        </Typography>
                      </>
                    )}
                  </Box>
                ),
              }}
              sx={{
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: COLORS.SAFEGUARD_BLUE,
                  fontWeight: 600,
                },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-overlayWrapper': {
                  minHeight: 200,
                },
                '& .MuiDataGrid-columnHeaders': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                },
                '& .MuiDataGrid-columnHeader .MuiCheckbox-root.Mui-checked': {
                  color: COLORS.BLUE_CRAYOLA,
                },
                '& .MuiDataGrid-columnHeader .MuiCheckbox-root.MuiCheckbox-indeterminate':
                  {
                    color: COLORS.BLUE_CRAYOLA,
                  },
                '& .MuiDataGrid-virtualScroller': {
                  overflowX: 'auto',
                  overflowY: 'auto',
                },
              }}
            />
          </Box>
        </Box>
      ) : (
        <>
          {/* Mobile: Card-based layout */}
          {rows.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
              }}
            >
              {joinRequestsError ? (
                <Typography
                  sx={{
                    color: COLORS.RED,
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {t(StringKeys.UNABLE_TO_LOAD_JOIN_REQUESTS)}
                </Typography>
              ) : (
                <>
                  <TaskAltIcon
                    sx={{
                      fontSize: 48,
                      color: COLORS.MODAL_HEADER_GRAY,
                      mb: 1,
                    }}
                  />
                  <Typography
                    sx={{
                      color: COLORS.MODAL_HEADER_GRAY,
                      fontSize: 14,
                    }}
                  >
                    {t(StringKeys.NO_PENDING_JOIN_REQUESTS)}
                  </Typography>
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ mt: 2, position: 'relative' }}>
              {processing && (
                <Box sx={processingOverlaySx}>
                  <CircularProgress />
                </Box>
              )}
              <Stack spacing={2} pb={5}>
                {rows.map((row) => (
                  <Card
                    key={row.id}
                    sx={{
                      backgroundColor: COLORS.WHITE,
                      boxShadow: 2,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Card Header */}
                    <Box
                      sx={{
                        backgroundColor: COLORS.SAFEGUARD_BLUE,
                        p: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="h6" sx={{ color: COLORS.WHITE }}>
                        {row.name}
                      </Typography>
                    </Box>

                    {/* Card Body */}
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        {/* Phone */}
                        <Grid size={6}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            {t(StringKeys.PHONE)}
                          </Typography>
                          <Typography sx={{ fontSize: 14 }}>
                            {row.phone}
                          </Typography>
                        </Grid>

                        {/* Date Requested */}
                        <Grid size={6}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            {t(StringKeys.DATE_REQUESTED)}
                          </Typography>
                          <Typography sx={{ fontSize: 14 }}>
                            {row.dateRequested}
                          </Typography>
                        </Grid>

                        {/* Role Selector */}
                        <Grid size={12}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                          >
                            {t(StringKeys.ASSIGN_ROLE)}
                          </Typography>
                          <Select
                            fullWidth
                            value={row.role}
                            onChange={(e) =>
                              handleRoleChange(row.id, e.target.value)
                            }
                            disabled={processing}
                            sx={{
                              backgroundColor: COLORS.TABLE_HEADER_GRAY,
                              '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                              },
                            }}
                          >
                            {roleOptions.map((role) => (
                              <MenuItem key={role} value={role}>
                                {role}
                              </MenuItem>
                            ))}
                          </Select>
                        </Grid>

                        {/* Group Selector */}
                        <Grid size={12}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            gutterBottom
                          >
                            {t(StringKeys.ASSIGN_GROUP)}
                          </Typography>
                          <Select
                            fullWidth
                            value={row.group}
                            onChange={(e) =>
                              handleGroupChange(row.id, e.target.value)
                            }
                            disabled={processing}
                            sx={{
                              backgroundColor: COLORS.TABLE_HEADER_GRAY,
                              '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                              },
                            }}
                          >
                            {(groupOptions.length > 0
                              ? groupOptions
                              : ['Default']
                            ).map((group) => (
                              <MenuItem key={group} value={group}>
                                {group}
                              </MenuItem>
                            ))}
                          </Select>
                        </Grid>

                        {/* Action Buttons */}
                        <Grid size={12}>
                          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                            <Button
                              variant="contained"
                              fullWidth
                              onClick={() => handleApprove(row.id)}
                              disabled={processing}
                              sx={{
                                backgroundColor: COLORS.BLUE_CRAYOLA,
                                '&:hover': {
                                  backgroundColor: '#2b64af',
                                },
                                textTransform: 'none',
                                fontWeight: 500,
                              }}
                            >
                              {t(StringKeys.APPROVE)}
                            </Button>
                            <Button
                              variant="outlined"
                              fullWidth
                              onClick={() => handleDeny(row.id)}
                              disabled={processing}
                              sx={{
                                borderColor: COLORS.RED,
                                color: COLORS.RED,
                                '&:hover': {
                                  borderColor: COLORS.BORDER_RED,
                                  backgroundColor: 'rgba(255, 0, 0, 0.04)',
                                },
                                textTransform: 'none',
                                fontWeight: 500,
                              }}
                            >
                              {t(StringKeys.DENY)}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default OrganizationJoinRequests;
